<script setup lang="ts">
/**
 * 代理商的授權申請（`/reseller/requests`，代理商落地頁）——批93-C §2.2。
 *
 * 這一頁就是代理商的工作台：**草稿改到對 → 確認送出 → 等我方開通**。
 * 四種狀態各自能做什麼，刻意寫死在畫面上而不是靠後端回錯誤才知道：
 * - **草稿**：可改內容、可確認送出、可撤回。被我方退回的草稿會多一塊紅底的退回理由。
 * - **待審核**：內容鎖定、唯讀；還可以撤回（客戶臨時說不用了）。
 * - **已核發**：附上簽出來的授權 id，到「授權」頁下載檔案交給客戶。
 * - **已拒絕／已取消**：只交代結果。
 *
 * ★ **keyset 分頁**（批93-D）：後端回 `{items, next_cursor}`，一頁 50 筆。
 *   寫入動作（建草稿／改／確認／撤回）之後一律 `loadRequests()` **回到第一頁**：
 *   剛動過的那筆一定在最上面。
 * ★★ **批121 §9：狀態頁籤下推伺服器**。在那之前頁籤是前端算的，於是①籤上的計數只算
 *   「已載入的那幾頁」②在某個籤上按「載入更多」載回的是下一頁**原始資料**、常常一列
 *   都沒增加，看起來像按了沒反應。現在換籤＝重新查詢，計數走
 *   `GET /reseller/license-requests/counts`（全表）。
 *   ★ 「換機申請」籤例外：那住另一張表（`rebind_requests`），量級是「客戶一生按幾次」，
 *     仍然一次載一頁、前端數。
 */
import { computed, onMounted, ref, watch } from 'vue'

import HostedApproveModal from '@/components/HostedApproveModal.vue'
import QuotaSummary from '@/components/QuotaSummary.vue'
import ResellerRequestForm from '@/components/ResellerRequestForm.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import type { LicenseReq, ProductSpec } from '@/api/ls'
import * as reseller from '@/api/reseller'
import type { ResellerCustomer, ResellerLicense } from '@/api/reseller'
import { activationPolicyLabel } from '@/lib/products'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { buildQuotaRows, planLabel } from '@/lib/quotas'
import { seedQuotas } from '@/lib/seedQuotas'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const dialogs = useDialogsStore()

const rows = ref<LicenseReq[]>([])
const customers = ref<ResellerCustomer[]>([])
const products = ref<ProductSpec[]>([])
const licenses = ref<ResellerLicense[]>([])
const loading = ref(true)
const loadError = ref('')
/** keyset 續頁游標（null＝已經載完）。 */
const nextCursor = ref<string | null>(null)
const loadingMore = ref(false)

/**
 * 目前這個籤要送給伺服器的 `status`。
 *
 * ★ 「換機申請」籤回空物件＝**不篩**：那個籤顯示的是另一張表（`rebind_requests`），
 *   授權申請這邊載哪一頁都不影響畫面，硬送一個 `status=rebind` 反而會被後端當未知值。
 */
function listQuery(): { status?: string } {
  return tab.value === 'rebind' ? {} : { status: tab.value }
}

/** 重載＝**回到第一頁**（剛動過的那筆一定在最上面）＋刷新籤上的全表計數。 */
async function loadRequests() {
  const [page, c] = await Promise.all([
    reseller.listRequests(listQuery()),
    reseller.myRequestCounts().catch(() => null), // 計數只是徽章，拿不到就留上一次的
  ])
  rows.value = page.items
  nextCursor.value = page.next_cursor
  if (c) serverCounts.value = c
  // 批108：換機單住另一張表，一起重載（量級是「客戶一生按幾次」，一頁就夠）。
  rebinds.value = (await reseller.listRebinds()).items
}

async function loadMore() {
  if (!nextCursor.value || loadingMore.value) return
  loadingMore.value = true
  try {
    // ★ 批121：帶著目前的籤續頁——不帶的話第二頁會變成「全部申請」而畫面看不出來。
    const page = await reseller.listRequests({ ...listQuery(), cursor: nextCursor.value })
    rows.value = [...rows.value, ...page.items]
    nextCursor.value = page.next_cursor
  } catch (e) {
    loadError.value = errText(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}

onMounted(async () => {
  try {
    const [reqs, cnts, custs, prods, lics] = await Promise.all([
      reseller.listRequests(listQuery()),
      reseller.myRequestCounts(),
      // 客戶下拉的來源——取一頁上限即可（代理商的客戶量級是幾十家）。
      reseller.listCustomers({ limit: 200 }),
      reseller.listProducts(),
      // 這裡只是「變更／續約／加購要挑哪一張現行證」的下拉來源——取一頁上限即可
      // （代理商的授權量級是幾十張；真的超過就在〈授權〉頁翻頁看）。
      reseller.listLicenses({ limit: 200 }),
    ])
    rows.value = reqs.items
    nextCursor.value = reqs.next_cursor
    serverCounts.value = cnts
    customers.value = custs.items
    products.value = prods
    licenses.value = lics.items
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取失敗')
  } finally {
    loading.value = false
  }
})

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  const obj = d as { message?: string; code?: string }
  return obj.message ?? obj.code ?? JSON.stringify(d)
}

// --- 頁籤 -------------------------------------------------------------------
type Tab = 'draft' | 'pending' | 'approved' | 'closed' | 'rebind'
const TABS: { key: Tab; label: string }[] = [
  { key: 'draft', label: '草稿' },
  { key: 'pending', label: '待審核' },
  { key: 'approved', label: '已核發' },
  { key: 'closed', label: '已結束' },
  // 批108：換機申請與授權申請是**兩張不同的表**（`rebind_requests`），所以它是獨立的桶，
  // 不進 `bucketOf`。代理商只送得出來，核准仍是我方的事。
  { key: 'rebind', label: '換機申請' },
]
// 批186：籤記住（本頁沒有其他過濾控制項，所以沒有「清除全部條件」——點〈草稿〉就是回到預設）。
const pf = usePersistedFilters('reseller-requests', { tab: 'draft' as Tab }, { oneOf: { tab: TABS.map((t) => t.key) } })
const { tab } = pf.refs
// ★ 批121：籤是**伺服器端**過濾，換籤＝重新查詢。放在這裡而不是檔案上方：`tab` 在
//   上面才宣告，提前 watch 是 TDZ 錯誤。
watch(tab, () => {
  void loadRequests().catch((e) => {
    loadError.value = errText(e, '讀取失敗')
  })
})
/**
 * 籤上的數字＝**伺服器算的全表筆數**（批121 §9），`rebind` 那一個例外（另一張表）。
 *
 * ★ 以前是把 `rows`（＝已載入的那幾頁）逐列分桶累加，申請一超過一頁就開始少算。
 * ★ 還沒拿到時退回已載入的筆數——寧可顯示「至少這麼多」也不要空白跳動。
 */
const serverCounts = ref<reseller.ResellerRequestCounts | null>(null)
const counts = computed<Record<Tab, number>>(() => {
  const c = serverCounts.value
  const n = rows.value.length
  return {
    draft: c ? c.draft : n,
    pending: c ? c.pending : n,
    approved: c ? c.approved : n,
    closed: c ? c.closed : n,
    rebind: rebinds.value.length,
  }
})
// ★ 批121：**不再前端過濾**——`rows` 已經是伺服器依 `status` 篩過的那一頁。留著再篩
//   一次會把「載入更多」載回來的列吃掉、看起來像按了沒反應。
const visible = computed(() => rows.value)

// --- 換機申請（批108）-------------------------------------------------------
//
// 與授權申請是**兩張不同的表**，所以獨立載、獨立顯示。代理商只送得出來
// （送件的入口在〈授權〉頁每一列的「申請換機」），核准仍是我方的事。
const rebinds = ref<reseller.ResellerRebind[]>([])
const REBIND_STATUS: Record<string, string> = {
  pending: '待審核',
  approved: '已核發',
  rejected: '已婉拒',
}
/** 被我方退回的草稿數（草稿頁籤裡最該先處理的那一批）。批121 起由伺服器算全表。 */
const returnedCount = computed(() => serverCounts.value?.returned ?? 0)

// --- 顯示 helper -------------------------------------------------------------
function customerName(id: string): string {
  return customers.value.find((c) => c.id === id)?.name ?? '—'
}
function productName(key: string): string {
  return products.value.find((p) => p.key === key)?.name ?? key
}
function specOf(key: string): ProductSpec | null {
  return products.value.find((p) => p.key === key) ?? null
}
const KIND_LABELS: Record<string, string> = {
  new: '新授權',
  change: '變更內容',
  renew: '續約',
  addon: '加購模組',
  // 批99-B：自家客戶在 portal 自助送的代管開站申請（B.3a）——代理商看得到、核可得了。
  hosted_provision: '代管開站',
}
function kindLabel(k: string | undefined): string {
  return KIND_LABELS[k ?? 'new'] ?? (k as string)
}
const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  pending: '待審核',
  approved: '已核發',
  rejected: '已拒絕',
  cancelled: '已取消',
}
function statusLabel(s: string): string {
  return STATUS_LABELS[s] ?? s
}
function statusClass(s: string): string {
  return (
    { draft: 'pending', pending: 'pending_activation', approved: 'active', rejected: 'revoked', cancelled: 'superseded' }[
      s
    ] ?? 'pending'
  )
}
/** 我方寫的理由（退回／婉拒／註記）——客戶端與代理商端看到的是同一段。 */
function reviewReason(r: LicenseReq): string | null {
  const fromField = r.review?.reason
  if (fromField) return String(fromField)
  const review = (r.request_json?.review ?? null) as { reason?: string | null } | null
  return review?.reason ? String(review.reason) : null
}
function fmtDateTime(iso: string | null | undefined): string {
  return iso ? iso.replace('T', ' ').slice(0, 16) : '—'
}

// --- 表單（建立／編輯草稿）---------------------------------------------------
const formOpen = ref(false)
const editingDraft = ref<LicenseReq | null>(null)
function openCreate() {
  editingDraft.value = null
  formOpen.value = true
}
function openEdit(r: LicenseReq) {
  editingDraft.value = r
  formOpen.value = true
}
async function afterSaved() {
  await loadRequests()
}

/**
 * 這筆申請要的配額（批114）。
 *
 * ★ 申請單的欄位名是「授權上的同名欄 ＋ `_requested` 尾綴」，所以把尾綴剝掉之後就是
 *   `lib/seedQuotas.ts` 認得的舊一級欄位名——**這樣這支不必寫出任何一個具體鍵名**
 *   （前端的零產品字面規則；鍵名的對照集中在 `seedQuotas` 那一個模組裡）。
 * ★ 為什麼要 `seedQuotas`：批114 之前建的草稿，數字是**一級欄位**、`quotas_requested`
 *   是空的；只讀後者的話那些數字會憑空消失。
 */
function requestedQuotas(r: LicenseReq): Record<string, number | null> | null {
  const flat = Object.fromEntries(
    Object.entries(r as unknown as Record<string, unknown>).map(([k, v]) => [
      k.replace(/_requested$/, ''),
      v,
    ]),
  )
  return seedQuotas(specOf(r.product), flat)
}

// --- 詳情（唯讀）------------------------------------------------------------
const detail = ref<LicenseReq | null>(null)
const detailRows = computed<{ k: string; v: string }[]>(() => {
  const r = detail.value
  if (!r) return []
  const reqd = (r.request_json?.requested ?? {}) as Record<string, unknown>
  const out: { k: string; v: string }[] = [
    { k: '客戶', v: customerName(r.customer_id) },
    { k: '產品', v: productName(r.product) },
    { k: '類型', v: kindLabel(r.kind) },
    { k: '主機', v: r.instance_name || r.host_instance_id || '—' },
    { k: '狀態', v: statusLabel(r.status) },
    { k: '送件人', v: r.requested_by || '—' },
    { k: '建立時間', v: fmtDateTime(r.created_at) },
  ]
  // ★ 這個 realm 的投影**不帶任何身分欄位**（`confirmed_by`／`reviewed_by`／`review.by`
  //   都被後端拿掉了）——「誰送的」看 `requested_by`，其餘只顯示時間。
  if (r.confirmed_at) out.push({ k: '確認送出', v: fmtDateTime(r.confirmed_at) })
  if (r.activation_policy) out.push({ k: '核發政策', v: activationPolicyLabel(r.activation_policy) })
  out.push({ k: '套裝', v: r.plan ? planLabel(specOf(r.product), r.plan) : '客製' })
  const mods = r.modules_requested ?? (reqd.modules as Record<string, string> | undefined)
  out.push({ k: '模組', v: Object.keys(mods ?? {}).sort().join('、') || '—' })
  // 批114：原本這裡①另外印一列硬編的數字欄、②配額直接印**英文鍵名**（`max_foo=3`）。
  // 改走 `buildQuotaRows`：中文名與欄序都由該產品的憑證設計決定，兩件事併成一列。
  const qrows = buildQuotaRows(specOf(r.product), requestedQuotas(r), null)
  out.push({
    k: '配額',
    v: qrows.map((q) => `${q.label} ${q.text}`).join('、') || '—',
  })
  if (r.note) out.push({ k: '備註', v: r.note })
  if (r.reviewed_at) out.push({ k: '我方處理', v: fmtDateTime(r.reviewed_at) })
  if (r.issued_license_id) out.push({ k: '核發的授權', v: r.issued_license_id })
  return out
})

// --- 代管開站申請（批99-B）--------------------------------------------------
//
// B.3a：`GET /reseller/license-requests` 除了「自家公司送的」，還會回**自家客戶在 portal
// 自助送的代管申請**（`kind === 'hosted_provision'`）。那種單子不是草稿、也不是用
// `ResellerRequestForm` 編輯的東西——它要選主機、跑容量檢查、當場開一整套站，
// 所以走**與我方後台同一個** `HostedApproveModal`（框內四支呼叫自己依 realm 分流）。
const hostedApproving = ref<LicenseReq | null>(null)
/** 這一列是不是代管開站申請。 */
function isHosted(r: LicenseReq): boolean {
  return r.kind === 'hosted_provision'
}
async function afterHostedApprove() {
  hostedApproving.value = null
  await loadRequests()
}
/**
 * 這一列撤得動嗎。
 *
 * ⚠️ 客戶自助送的代管申請**撤不動**：`POST /reseller/license-requests/{id}/cancel` 撤的是
 *   「自家公司送出的」（`license_requests.reseller_id == 我`），而 portal 送的那一欄是 NULL
 *   （B.3a 刻意不寫入）。對這些單子，代理商的出口是「婉拒」（在核可框裡）。
 */
function canCancelRow(r: LicenseReq): boolean {
  if (r.status !== 'draft' && r.status !== 'pending') return false
  return !isHosted(r) || (r.origin ?? '') === 'reseller'
}

// --- 撤回 -------------------------------------------------------------------
const busyId = ref<string | null>(null)
async function cancel(r: LicenseReq) {
  const ok = await dialogs.confirm({
    title: '撤回申請',
    message:
      r.status === 'pending'
        ? `撤回「${customerName(r.customer_id)}」這筆待審核的申請？我方就不會再處理它了。`
        : `刪掉這份草稿？內容不會保留。`,
    danger: true,
    confirmText: '撤回',
  })
  if (!ok) return
  busyId.value = r.id
  try {
    await reseller.cancelRequest(r.id)
    detail.value = null
    await loadRequests()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '撤回失敗') })
  } finally {
    busyId.value = null
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>授權申請</h1>
      <span class="count">{{ rows.length }}</span>
      <span class="spacer" />
      <UiButton variant="primary" :disabled="loading" @click="openCreate">＋ 建立授權草稿</UiButton>
    </div>

    <p v-if="loadError" class="err">{{ loadError }}</p>
    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>

    <template v-else>
      <p v-if="returnedCount && tab === 'draft'" class="returned-hint">
        有 {{ returnedCount }} 份草稿被我方退回並附了理由——修改後重新確認送出即可。
      </p>

      <div class="card">
        <div class="tabs filters" data-hscroll>
          <button
            v-for="t in TABS"
            :key="t.key"
            type="button"
            class="tab"
            :class="{ active: tab === t.key }"
            @click="tab = t.key"
          >
            {{ t.label }}<span class="tab-n">{{ counts[t.key] }}</span>
          </button>
        </div>

        <!-- 批108：換機申請（獨立的表，不走 `visible`／`bucketOf`） -->
        <template v-if="tab === 'rebind'">
          <div v-if="!rebinds.length" class="empty">
            <div class="empty-sub">
              目前沒有換機申請。客戶換了機器時，到〈授權〉頁該列按「申請換機」送件
              ——核准由愛+1 處理，核准後回這一頁就看得到新證。
            </div>
          </div>
          <div v-else class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table">
              <thead>
                <tr><th>客戶</th><th>原授權</th><th>狀態</th><th>核准後的新證</th><th>時間</th></tr>
              </thead>
              <tbody>
                <tr v-for="r in rebinds" :key="r.id">
                  <td><strong>{{ r.customer_name ?? '—' }}</strong></td>
                  <td><code class="mono">{{ (r.old_license_id ?? '').slice(0, 8) || '—' }}</code></td>
                  <td>
                    <span class="status-pill" :class="r.status">
                      {{ REBIND_STATUS[r.status] ?? r.status }}
                    </span>
                  </td>
                  <td>
                    <code v-if="r.issued_license_id" class="mono">{{ r.issued_license_id.slice(0, 8) }}</code>
                    <span v-else class="subtle">—</span>
                    <div v-if="r.issued_license_id" class="subtle sub-line">
                      到〈授權〉頁用「下載授權檔」交給客戶
                    </div>
                  </td>
                  <td class="small">{{ r.created_at.slice(0, 16).replace('T', ' ') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <div v-else-if="!visible.length" class="empty">
          <div class="empty-sub">
            {{
              tab === 'draft'
                ? '沒有草稿。按右上角「建立授權草稿」開始一筆新的申請。'
                : '這個分類目前沒有申請。'
            }}
          </div>
        </div>
        <div v-else class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead>
              <tr>
                <th>客戶</th><th>產品</th><th>類型</th><th>套裝／配額</th>
                <th>狀態</th><th>時間</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in visible" :key="r.id">
                <td>
                  <strong>{{ customerName(r.customer_id) }}</strong>
                  <div v-if="r.instance_name || r.host_instance_id" class="subtle sub-line">
                    {{ r.instance_name || r.host_instance_id?.slice(0, 12) }}
                  </div>
                </td>
                <td class="nowrap">{{ productName(r.product) }}</td>
                <td class="nowrap">{{ kindLabel(r.kind) }}</td>
                <td class="planquota">
                  {{ r.plan ? planLabel(specOf(r.product), r.plan) : '客製' }}
                  <!-- 批114：原本是硬編的單一數字欄。改成申請內容的配額摘要（前 2 項＋`+N`）。 -->
                  <div class="subtle sub-line">
                    <QuotaSummary :spec="specOf(r.product)" :quotas="requestedQuotas(r)" />
                  </div>
                </td>
                <td>
                  <span class="status-pill" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
                  <div v-if="reviewReason(r)" class="reason" :title="reviewReason(r) ?? ''">
                    {{ r.status === 'draft' ? '我方退回：' : '' }}{{ reviewReason(r) }}
                  </div>
                </td>
                <td class="nowrap tsmall">{{ fmtDateTime(r.confirmed_at ?? r.created_at) }}</td>
                <td class="acts-cell">
                  <div class="acts">
                    <!-- 代管開站申請走另一個框（選主機＋容量檢查＋核可就開站）；
                         待審的標「檢視／核可」，已審的仍是「檢視」（框內自己唯讀）。 -->
                    <UiButton
                      v-if="isHosted(r)"
                      :variant="r.status === 'pending' ? 'primary' : 'secondary'"
                      size="sm"
                      @click="hostedApproving = r"
                    >
                      {{ r.status === 'pending' ? '檢視／核可' : '檢視' }}
                    </UiButton>
                    <UiButton
                      v-else-if="r.status === 'draft'"
                      variant="primary"
                      size="sm"
                      @click="openEdit(r)"
                    >
                      修改／送出
                    </UiButton>
                    <UiButton v-else variant="secondary" size="sm" @click="detail = r">檢視</UiButton>
                    <UiButton
                      v-if="canCancelRow(r)"
                      variant="danger"
                      size="sm"
                      :loading="busyId === r.id"
                      @click="cancel(r)"
                    >
                      撤回
                    </UiButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- keyset 分頁：後端一頁 50 筆。`next_cursor` 非 null 才畫這一列。
             ★ 批121：文案是「已顯示 N / 總數」——分母是**這個籤**的全表筆數。 -->
        <div v-if="nextCursor" class="more">
          <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
          <span class="subtle">已顯示 {{ rows.length }} / {{ counts[tab] }} 筆</span>
        </div>
      </div>
    </template>

    <!-- 建立／編輯草稿（同一支表單） -->
    <ResellerRequestForm
      :key="`form-${editingDraft?.id ?? 'new'}-${formOpen}`"
      :open="formOpen"
      :draft="editingDraft"
      :customers="customers"
      :products="products"
      :licenses="licenses"
      @close="formOpen = false"
      @saved="afterSaved"
    />

    <!-- 代管開站申請的核可框（批99-B）：與我方後台共用同一支元件 -->
    <HostedApproveModal
      :request="hostedApproving"
      :customer-name="hostedApproving ? customerName(hostedApproving.customer_id) : ''"
      @close="hostedApproving = null"
      @done="afterHostedApprove"
    />

    <!-- 唯讀詳情（已送出之後的內容一律鎖定） -->
    <UiModal
      :open="!!detail"
      width="640px"
      :title="`申請詳情 — ${detail ? customerName(detail.customer_id) : ''}`"
      @close="detail = null"
    >
      <template v-if="detail">
        <div class="ro">
          <div v-for="row in detailRows" :key="row.k" class="ro-row">
            <span class="ro-k">{{ row.k }}</span><span class="ro-v">{{ row.v }}</span>
          </div>
        </div>
        <p v-if="reviewReason(detail)" class="reason-box">
          <strong>我方說明：</strong>{{ reviewReason(detail) }}
        </p>
        <p v-if="detail.status === 'pending'" class="subtle">
          內容已鎖定，等待我方審核。需要改內容請先撤回，或聯絡我方退回草稿。
        </p>
      </template>
      <template #foot>
        <UiButton @click="detail = null">關閉</UiButton>
        <UiButton
          v-if="detail && detail.status === 'pending'"
          variant="danger"
          :loading="busyId === detail.id"
          @click="cancel(detail)"
        >
          撤回
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.err {
  color: var(--danger);
  font-size: var(--fs-13);
}
.returned-hint {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--warning);
  border-radius: var(--r-sm);
  color: var(--warning);
  font-size: var(--fs-12);
}
.filters {
  margin-bottom: var(--sp-3);
}
.tab-n {
  margin-left: 6px;
  color: var(--fg-4);
  font-size: var(--fs-11);
}
.acts {
  display: flex;
  gap: 6px;
  align-items: center;
}
.nowrap {
  white-space: nowrap;
}
.tsmall {
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.sub-line {
  margin-top: 2px;
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
/* 批114：套裝＋配額摘要同一格。摘要自己 nowrap＋省略號，這裡只壓欄寬（1366 版面）。 */
.planquota {
  max-width: 220px;
}
.reason {
  margin-top: 3px;
  max-width: 260px;
  font-size: var(--fs-11);
  color: var(--fg-3);
  line-height: var(--lh-base);
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.ro {
  display: grid;
  gap: 4px;
  padding: var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--bg);
}
.ro-row {
  display: flex;
  gap: var(--sp-3);
  font-size: var(--fs-13);
}
.ro-k {
  color: var(--fg-3);
  min-width: 84px;
  flex: none;
}
.ro-v {
  color: var(--fg);
  word-break: break-word;
}
.reason-box {
  margin: var(--sp-3) 0 0;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--warning);
  border-radius: var(--r-sm);
  color: var(--fg-2);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
/* keyset 分頁的「載入更多」列（形狀與 ResellerLicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
</style>
